import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  InputAdornment,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Label from "../../../../../common/components/Label";
import {
  IJobLocations,
  ILocationResponse,
  TJobMode,
} from "../../../../../types/campaign.types";
import StackWrapper from "../../StackWrapper";
import SelectWrapper from "../../SelectWrapper";
import JobLocationsSelector from "./JobLocationsSelector";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import JobLocationChips from "./JobLocationChips";
import CommonTooltip from "../../../../../common/components/CommonTooltip";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";

interface IProps {
  jobMode: TJobMode;
  selectedJobLocations: IJobLocations[];
  jobLocations: ILocationResponse | null;
  handleUpdateJobMode: (jobMode: string) => void;
  handleUpdateJobLocations: (selectedLocations: IJobLocations[]) => void;
  error: string;
}

const JobModeOptions: { value: TJobMode; label: string }[] = [
  { label: "WFO (Work from office)", value: "WFO" },
  { label: "Hybrid", value: "Hybrid" },
  { label: "Remote", value: "Remote" },
];

const StyledInputAdornment = styled(InputAdornment)`
  flex-wrap: wrap;
  flex-direction: column;
  height: auto;
  margin: 0;
  gap: 0.4rem;
  padding: 1.2rem 0;
  width: auto;
  > span {
    display: none;
  }
  .MuiChip-root {
    width: auto;
  }
  .MuiChip-label {
    padding-left: 0.8rem;
    padding-right: 0.4rem;
  }
  .MuiSvgIcon-root {
    margin: 0 0.5rem 0 0 !important;
  }
`;

const JobLocationWrapper: React.FC<IProps> = ({
  jobMode,
  selectedJobLocations,
  jobLocations,
  handleUpdateJobMode,
  handleUpdateJobLocations,
  error,
}) => {
  const [inputValue, setInputValue] = useState<string>();
  const [filteredJobLocations, setFilteredJobLocations] =
    useState<ILocationResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [isBlur, setIsBlur] = useState<boolean>(false);
  const inputRef = useRef<HTMLDivElement | null>(null);

   const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
    const  selLocation  = useSelector(
    (state:RootState) => state.campaign.disabledFields.jobLocations
  );
  const disableJobLocation = ((queryParams.get("triggeredFromPage") === 'SRP' && selLocation )) ? true : false

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e?.target?.value || "";
    setInputValue(value);

    // filter jobLocations based on input value
    const filteredLocations: ILocationResponse | null = jobLocations
      ? { ...jobLocations }
      : null;

    if (filteredLocations) {
      filteredLocations.topCities =
        filteredLocations.topCities?.filter((item) =>
          item.value?.toLowerCase()?.includes(value?.toLowerCase())
        ) || [];
      filteredLocations.internationalLocations =
        filteredLocations.internationalLocations?.filter((item) =>
          item.value?.toLowerCase()?.includes(value?.toLowerCase())
        ) || [];
      filteredLocations.regionAndStates =
        filteredLocations?.regionAndStates?.map((regionAndState) => {
          const filteredCities =
            regionAndState?.cities?.filter((item) =>
              item.value?.toLowerCase()?.includes(value?.toLowerCase())
            ) || [];
          return { ...regionAndState, cities: filteredCities };
        }) || [];

      filteredLocations.regionAndStates =
        filteredLocations.regionAndStates.filter((item) => item.cities?.length);
    }

    if (!value) {
      setFilteredJobLocations(jobLocations);
    } else {
      setFilteredJobLocations(filteredLocations);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleBlur = () => {
    setIsBlur(true);
  };

  const handleSelectJobLocations = (selectedLocations: IJobLocations[]) => {
    handleUpdateJobLocations(selectedLocations);
    setInputValue("");
    setFilteredJobLocations(jobLocations);
  };

  const handleRemoveChip = (id: string) => {
    const filteredJobLocations = selectedJobLocations?.filter(
      (item) => item.id !== id
    );
    handleUpdateJobLocations(filteredJobLocations);
  };

  useEffect(() => {
    setFilteredJobLocations(jobLocations);
  }, [jobLocations]);

  useEffect(() => {
    if (isBlur && !open && !selectedJobLocations?.length) {
      handleUpdateJobLocations([]);
      setIsBlur(false);
    }
  }, [handleUpdateJobLocations, isBlur, open, selectedJobLocations?.length]);

  return (
    <StackWrapper>
      <Label
        colorType="light"
        label="Job mode & location"
        required
        labelFor="jobLocation"
      />

      <ClickAwayListener onClickAway={handleClose}>
        <Box
          sx={{
            position: "relative",
            borderRadius: "4px",
            // border:disableJobLocation ? 'none' : `1px solid ${COLOR_MAP.BORDER_GREY}`,
            border:`1px solid ${COLOR_MAP.BORDER_GREY}`,
            ...(!!error && !selectedJobLocations?.length
              ? {
                  borderColor: COLOR_MAP.ERROR,
                }
              : {}),
            background: "#FFF",
          }}
        >
          <Box sx={{ display: "flex" }}>
            {/* Job mode */}
            <Box
              sx={{
                width: "100px",
                textOverflow: "inherit",
                color: COLOR_MAP.GREY_5,
                background: COLOR_MAP.GREY_9,
                borderRadius: "4px 0 0 4px",
              }}
            >
              <SelectWrapper
                handleChange={handleUpdateJobMode}
                value={jobMode}
                menuItems={JobModeOptions}
                renderValue={(value) => (value === "WFO" ? "WFO" : value)}
                sx={{
                  width: "100px",
                  color: COLOR_MAP.GREY_5,
                  background: COLOR_MAP.GREY_9,
                  paddingRight: "0 !important",
                  "& .MuiOutlinedInput-notchedOutline": {
                    // borderLeft: "none",
                    // borderRight: `1px solid ${COLOR_MAP.BORDER_GREY}`,
                    border:'none',
                    borderBottomRightRadius: 0,
                    borderTopRightRadius: 0,
                  },
                }}
              />
            </Box>

            {/* Job Location */}
            <Box sx={{ flexGrow: 1, display: "inline-block" }}>
                <CommonTooltip 
                  title={jobMode === 'Remote' ? 'Remote location has been selected' : disableJobLocation ? 'Search filters cannot be edited during campaign setup.' : ''}
                  width='270px'
                  customZIndex={99999999}
                  offset={[0, 10]}
              >
                <TextField
                  value={inputValue}
                  id="jobLocationInput"
                  onChange={handleInputChange}
                  sx={{
                    paddingLeft: 0,
                    "& fieldset": { border: "none" },
                    "& .MuiInputBase-input":{
                      borderRadius: '0 4px 4px 0',
                    },
                    "& .MuiInputAdornment-root": {
                      padding: 0,
                    },
                    "& .MuiInputBase-root": {
                      borderRadius:'0 4px 4px 0',
                      borderLeft:disableJobLocation ? '1px solid #D2D1D6' : 'none',
                    },
                     "& .Mui-disabled":{
                    backgroundColor:'#F6F6F7'
                  }
                  }}
                  disabled={jobMode === "Remote" || disableJobLocation}
                  onClick={()=>{
                    if(disableJobLocation) return
                    handleOpen()
                  }}
                  onBlur={handleBlur}
                  placeholder={
                    !selectedJobLocations?.length ? "Select location" : ""
                  }
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <StyledInputAdornment position="start">
                        {jobMode !== "Remote" && (
                          <JobLocationChips
                            reference={inputRef}
                            data={selectedJobLocations}
                            handleRemoveChip={handleRemoveChip}
                            handleOpen={handleOpen}
                            disabled={disableJobLocation}
                          />
                        )}
                      </StyledInputAdornment>
                    ),
                    inputProps: {
                      sx: {
                        cursor:
                          jobMode === "Remote" ? "not-allowed" : "pointer",
                      },
                    },
                    sx: {
                      p: 0,
                    },
                  }}
                  ref={inputRef}
                />
              </CommonTooltip>
            </Box>
          </Box>
          {open && !!filteredJobLocations && (
            <JobLocationsSelector
              data={filteredJobLocations}
              handleUpdateJobLocations={handleSelectJobLocations}
              jobLocations={selectedJobLocations}
            />
          )}
        </Box>
      </ClickAwayListener>
      {!!error && !selectedJobLocations.length && (
        <Typography sx={{ color: COLOR_MAP.ERROR, fontSize: 12 }}>
          {error}
        </Typography>
      )}
    </StackWrapper>
  );
};

export default React.memo(JobLocationWrapper);
